import { BLACK } from '../../../constants/color';
import {
  TRANSIT_PRINT_DATE_FONT_SIZE,
  TRANSIT_PRINT_FONT_SIZE,
  TRANSIT_PRINT_LABEL_FONT_SIZE,
  TRANSIT_PRINT_TITLE_FONT_SIZE,
} from '../../../constants/printoutConstants';
import { TransitOrderData, TransitPrintoutData } from '../../../types';

type TransitPrintoutProps = {
  data: TransitPrintoutData;
};

const BORDER = `1px solid ${BLACK}`;

// 宽度擑满可打印宽度；高度由 .transit-page 控制（打印时 99vh，纸张多大就铺多大）
const PAGE_WIDTH = '100%';

const cellStyle: React.CSSProperties = {
  border: BORDER,
  padding: '16px 22px',
  fontSize: TRANSIT_PRINT_FONT_SIZE,
  fontFamily: 'monospace',
  verticalAlign: 'top',
  wordBreak: 'break-word',
  lineHeight: 1.3,
};

/** SHIPPER / CONSIGNEE / TRANSPORT COMPANY / PICK UP DATE 这类小标题 */
const labelStyle: React.CSSProperties = {
  fontSize: TRANSIT_PRINT_LABEL_FONT_SIZE,
  fontWeight: 'bold',
  fontFamily: 'monospace',
};

/** 小标题下方的每一行内容 */
const lineStyle: React.CSSProperties = {
  marginTop: 6,
};

/** 小标题下的第一行（店铺名/收货人）：留空并加粗 */
const firstLineStyle: React.CSSProperties = {
  marginTop: 24,
  fontWeight: 'bold',
};

/** 底部SKID数量：加大加粗 */
const skidStyle: React.CSSProperties = {
  fontSize: TRANSIT_PRINT_LABEL_FONT_SIZE,
  fontWeight: 'bold',
};

/** 自提/送货日期：加大加粗 */
const pickUpDateStyle: React.CSSProperties = {
  marginTop: 40,
  fontSize: TRANSIT_PRINT_DATE_FONT_SIZE,
  fontWeight: 'bold',
};

/**
 * Transit单：Letter横向，一个订单一页，整页铺满
 * 顶部整行为店铺Logo + TRANSIT标题；
 * 中间左右分别为SHIPPER(店铺)与CONSIGNEE(收货人)；
 * 左下留空手写承运商，右下为自取/送货日期；最底部整行手写SKID数量
 */
const TransitPrintout = ({ data }: TransitPrintoutProps) => {
  // Shipper优先取订单自身抬头（logo为""时表示不显示，不能回退），旧接口无此字段时取顶层
  const getShipper = (order: TransitOrderData) => ({
    logo: order.logo !== undefined ? order.logo : data.logo,
    businessName:
      order.businessName !== undefined ? order.businessName : data.businessName,
    businessAddress:
      order.businessAddress !== undefined
        ? order.businessAddress
        : data.businessAddress,
    businessPhone:
      order.businessPhone !== undefined ? order.businessPhone : data.businessPhone,
  });

  const getPage = (order: TransitOrderData, shipper = getShipper(order)) => (
    <table
      className="transit-page"
      style={{
        width: PAGE_WIDTH,
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
      }}
    >
      <tbody>
        <tr style={{ height: '18%' }}>
          <td
            colSpan={2}
            style={{ ...cellStyle, verticalAlign: 'middle' }}
          >
            <div style={{ position: 'relative', textAlign: 'center' }}>
              {shipper.logo && (
                <img
                  src={shipper.logo}
                  alt="logo"
                  width={260}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                />
              )}
              <span
                style={{
                  ...labelStyle,
                  fontSize: TRANSIT_PRINT_TITLE_FONT_SIZE,
                }}
              >
                TRANSIT
              </span>
            </div>
          </td>
        </tr>
        <tr style={{ height: '38%' }}>
          <td style={{ ...cellStyle, width: '50%' }}>
            <div style={labelStyle}>SHIPPER</div>
            <div style={firstLineStyle}>{shipper.businessName}</div>
            <div style={lineStyle}>{shipper.businessAddress}</div>
            <div style={lineStyle}>{shipper.businessPhone}</div>
          </td>
          <td style={{ ...cellStyle, width: '50%' }}>
            <div style={labelStyle}>CONSIGNEE</div>
            <div style={firstLineStyle}>
              {order.orderAddress && order.orderAddress.consignee}
            </div>
            <div style={lineStyle}>
              {order.orderAddress && order.orderAddress.fullAddress}
            </div>
            <div style={lineStyle}>
              {order.orderAddress && order.orderAddress.mobile}
            </div>
          </td>
        </tr>
        <tr style={{ height: '26%' }}>
          {/* 承运商：只印标题，下方留空手写 */}
          <td style={cellStyle}>
            <div style={labelStyle}>TRANSPORT COMPANY</div>
          </td>
          <td style={cellStyle}>
            <div style={labelStyle}>PICK UP DATE</div>
            <div style={pickUpDateStyle}>
              {order.orderShipping &&
                order.orderShipping.formattedExpectShippingTime}
            </div>
          </td>
        </tr>
        <tr style={{ height: '18%' }}>
          {/* SKID数量：整行留空手写 */}
          <td
            colSpan={2}
            style={{
              ...cellStyle,
              textAlign: 'center',
              verticalAlign: 'middle',
              ...skidStyle,
            }}
          >
            _______ OF _______ SKID(S)
          </td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <>
      {data.transitOrderList &&
        data.transitOrderList.map((order, index) => (
          <div style={{ margin: 0 }} key={order.orderId}>
            {/* 一单一页 */}
            {index > 0 && <div className="page-break" />}
            {getPage(order)}
          </div>
        ))}
    </>
  );
};

export default TransitPrintout;
